import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import api from '../config/api';

const PROD_FALLBACK = 'Unable to connect. The server may be starting—wait 60 seconds and try again.';
const sanitizeError = (msg) => {
  if (!msg || typeof msg !== 'string') return msg;
  const isProd = typeof window !== 'undefined' && !/^localhost$|^127\./.test(window.location?.hostname || '');
  if (isProd && (msg.toLowerCase().includes('localhost') || msg.includes('5001'))) return PROD_FALLBACK;
  return msg;
};

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    school: '',
    hobbies: '',
    majors: '',
    city: 'Seattle',
    street: '',
    preferences: [],
    lookingFor: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFacebookReady, setIsFacebookReady] = useState(false);
  // Temporary fallback - remove after env var is confirmed working
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '1157569159785704';
  const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID || '';
  // Temporary hardcoded fallback for X - remove after env var is confirmed working
  const xClientId = process.env.REACT_APP_X_CLIENT_ID || 'QXJrU2FuS2VHMENXbC1XWGZocWk6MTpjaQ';
  const navigate = useNavigate();

  // Debug: Log Facebook App ID on component mount
  useEffect(() => {
    console.log('🔍 Facebook App ID check:', {
      'REACT_APP_FACEBOOK_APP_ID': process.env.REACT_APP_FACEBOOK_APP_ID,
      'facebookAppId': facebookAppId,
      'isSet': !!facebookAppId
    });
    console.log('🔍 X Client ID check:', {
      'REACT_APP_X_CLIENT_ID': process.env.REACT_APP_X_CLIENT_ID,
      'xClientId': xClientId,
      'isSet': !!xClientId
    });
  }, [facebookAppId, xClientId]);

  // Handle X OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const xLoginSuccess = urlParams.get('x_login_success');
    const sessionId = urlParams.get('sessionId');
    const userParam = urlParams.get('user');
    const error = urlParams.get('error');
    const errorDetails = urlParams.get('details');

    console.log('🔍 X OAuth Callback Handler:', {
      xLoginSuccess,
      hasSessionId: !!sessionId,
      hasUserParam: !!userParam,
      error,
      errorDetails,
      fullSearch: window.location.search,
      allParams: Object.fromEntries(urlParams.entries())
    });
    
    // If there's an error, log it prominently
    if (error) {
      console.error('❌ X LOGIN ERROR DETECTED:', {
        errorType: error,
        errorDetails: errorDetails || 'No details provided',
        fullURL: window.location.href,
        searchParams: window.location.search
      });
    }

    if (xLoginSuccess === 'true' && sessionId && userParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('user', JSON.stringify(userData));
        if (onLogin) onLogin(userData, sessionId);
        window.history.replaceState({}, document.title, '/login');
        navigate('/');
      } catch (err) {
        console.error('Error parsing X login callback:', err);
        setError('Failed to complete X login. Please try again.');
      }
    } else if (error) {
      let errorMsg = 'X login failed. ';
      if (error === 'no_code') {
        errorMsg += 'No authorization code received.';
      } else if (error === 'not_configured') {
        errorMsg += 'X OAuth is not configured on the server.';
      } else if (error === 'x_auth_failed') {
        // Use details if available, otherwise show helpful message
        if (errorDetails) {
          errorMsg += errorDetails;
        } else {
          errorMsg += 'X authentication failed. This usually means: (1) Server was restarted between authorization, (2) Redirect URI mismatch in X Developer Portal, or (3) Invalid credentials. Check server console for details.';
        }
      } else if (error === 'x_invalid_credentials') {
        errorMsg += errorDetails || 'X authentication failed: Invalid credentials. Please check server configuration.';
      } else if (error === 'x_invalid_request') {
        errorMsg += errorDetails || 'X authentication failed: Invalid request. Please try again.';
      } else if (error === 'x_oauth_error') {
        errorMsg += 'X OAuth error: ' + (errorDetails || 'Authorization was denied or failed.');
      } else if (error === 'no_state') {
        errorMsg += 'X authentication failed: Missing state parameter. Please try again.';
      } else if (error === 'create_failed') {
        errorMsg += errorDetails || 'Failed to create user account. Please try again.';
      } else {
        // Generic error - include details if available
        errorMsg += errorDetails ? errorDetails : 'Unknown error occurred. Please check server console for details.';
      }
      console.error('❌ X Login Error:', { error, errorDetails, fullErrorMsg: errorMsg });
      console.error('📋 Full Error Info:', {
        errorType: error,
        errorDetails: errorDetails,
        decodedDetails: errorDetails ? decodeURIComponent(errorDetails) : null,
        urlSearch: window.location.search
      });
      
      // Show error with details prominently
      setError(sanitizeError(errorMsg));
      
      // Keep error in URL for debugging (don't clear immediately)
      // User can manually refresh to clear
    }
  }, [navigate, onLogin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      const currentPrefs = formData.preferences || [];
      if (checked) {
        setFormData({ ...formData, preferences: [...currentPrefs, value] });
      } else {
        setFormData({ ...formData, preferences: currentPrefs.filter(p => p !== value) });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (isLogin) {
      if (!formData.email.trim() || !formData.password.trim()) {
        setError('Please enter both email and password');
        return;
      }
    } else {
      if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
        setError('Please fill in all required fields');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
    }
    
    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await api.post('/auth/login', {
          email: formData.email.trim(),
          password: formData.password
        });
        
        if (response.data.success) {
          localStorage.setItem('sessionId', response.data.sessionId);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          if (onLogin) onLogin(response.data.user, response.data.sessionId);
          navigate('/');
        }
      } else {
        const hobbiesArray = formData.hobbies.split(',').map(h => h.trim()).filter(Boolean);
        const majorsArray = formData.majors.split(',').map(m => m.trim()).filter(Boolean);
        
        const response = await api.post('/auth/register', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          school: formData.school || null,
          hobbies: hobbiesArray,
          majors: majorsArray,
          location: {
            state: 'WA',
            city: 'Seattle',
            street: formData.street
          },
          preferences: formData.preferences,
          lookingFor: formData.lookingFor
        });
        
        if (response.data.success) {
          localStorage.setItem('sessionId', response.data.sessionId);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          if (onLogin) onLogin(response.data.user, response.data.sessionId);
          navigate('/');
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Backend not ready. Wait 30–60 seconds for server to wake up, then try again.');
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError('Unable to connect. Server may be starting—wait a minute and try again.');
      } else if (err.response?.data?.error) {
        setError(sanitizeError(err.response.data.error));
      } else if (err.response?.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.error || 'Please check your input and try again.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('timeout')) {
        setError('Request timed out. Server may be starting—wait a minute and try again.');
      } else {
        setError(sanitizeError(err.response?.data?.error || err.message) || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credential) => {
    setError('');
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/google', {
        credential: credential
      });
      
      console.log('📥 Server response received:', response.status, response.data);
      
      console.log('✅ Google OAuth response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Login successful, storing session...');
        localStorage.setItem('sessionId', response.data.sessionId);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (onLogin) onLogin(response.data.user, response.data.sessionId);
        console.log('✅ Redirecting to home...');
        navigate('/');
      } else {
        console.error('❌ Login failed - no success flag');
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ ========== Google OAuth Error ==========');
      console.error('Full error object:', err);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error headers:', err.response?.headers);
      console.error('Request URL:', err.config?.url);
      console.error('Request method:', err.config?.method);
      console.error('Request data:', err.config?.data);
      
      if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        const errorDetails = err.response.data.details;
        console.error('Server error message:', errorMsg);
        console.error('Server error details:', errorDetails);
        setError(sanitizeError(errorMsg + (errorDetails ? ' (' + errorDetails + ')' : '')));
      } else if (err.response?.status === 404) {
        const isProd = !window.location.hostname.match(/^localhost$|^127\./);
        setError(isProd
          ? 'Backend not ready. Wait 60s for Render to wake up, then try again.'
          : `Route not found. Restart server: cd server && node index.js`);
      } else if (err.response?.status === 400) {
        setError('Invalid Google credential. Please try signing in again.');
      } else if (err.response?.status === 500) {
        setError('Server error during authentication. Please check server logs.');
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError('Unable to connect. Wait 60s for backend to wake up, then try again.');
      } else if (!err.response) {
        setError('No response. Render may be starting—wait 60s and try again.');
      } else {
        setError(sanitizeError('Failed to sign in with Google: ' + (err.message || 'Unknown error')));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google OAuth errors
  useEffect(() => {
    const handleGoogleError = (error) => {
      if (error.type === 'popup_closed' || error.type === 'popup_blocked') {
        setError('Google sign-in popup was blocked or closed. Please try again.');
      } else if (error.type === 'idpiframe_initialization_failed') {
        const origin = window.location.origin;
        setError(
          `Google OAuth setup error. Please add "${origin}" to Authorized JavaScript origins in Google Cloud Console. ` +
          'Go to: https://console.cloud.google.com/apis/credentials'
        );
      }
    };

    // Listen for Google OAuth errors
    window.addEventListener('message', (event) => {
      if (event.data && event.data.type && event.data.type.startsWith('gs-')) {
        if (event.data.type === 'gs-error') {
          handleGoogleError(event.data);
        }
      }
    });

    return () => {
      window.removeEventListener('message', handleGoogleError);
    };
  }, []);

  useEffect(() => {
    // Load Google Identity Services
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google && window.google.accounts) {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '509595359009-v3n6lm7m0k1p8k8a34s2sllvh3jsd4fm.apps.googleusercontent.com';
        const currentOrigin = window.location.origin;
        
        // Log for debugging
        console.log('🔍 Google OAuth Debug Info:');
        console.log('Current Origin:', currentOrigin);
        console.log('Client ID:', clientId);
        console.log('Full URL:', window.location.href);
        console.log('⚠️ Make sure this origin is added to Google Cloud Console:');
        console.log('   Authorized JavaScript origins:', currentOrigin);
        console.log('   Authorized redirect URIs:', currentOrigin);
        
        if (clientId) {
          try {
            window.google.accounts.id.initialize({
              client_id: clientId,
              callback: handleGoogleLogin,
              error_callback: (error) => {
                console.error('Google OAuth Error:', error);
                if (error.type === 'idpiframe_initialization_failed') {
                  setError(
                    `⚠️ OAuth Configuration Required: Please add "${currentOrigin}" to Authorized JavaScript origins in Google Cloud Console. ` +
                    `Visit: https://console.cloud.google.com/apis/credentials and edit your OAuth Client ID.`
                  );
                } else if (error.type === 'popup_failed' || error.type === 'popup_closed') {
                  // User closed popup, don't show error
                  return;
                } else {
                  let errorMsg = `Google sign-in error: ${error.type || 'Unknown error'}`;
                  if (error.type && error.type.includes('invalid_client')) {
                    errorMsg = `Invalid Client ID. Please verify your Client ID "${clientId}" is correct in Google Cloud Console. ` +
                      `Make sure it's a "Web application" type OAuth client. Visit: https://console.cloud.google.com/apis/credentials`;
                  }
                  setError(sanitizeError(errorMsg));
                }
              }
            });
            
            // Render the button with error handling
            const buttonDiv = document.getElementById('google-signin-button');
            if (buttonDiv) {
              try {
                window.google.accounts.id.renderButton(
                  buttonDiv,
                  { 
                    theme: 'outline', 
                    size: 'large', 
                    text: 'signin_with',
                    width: '100%',
                    shape: 'pill',
                    onError: (error) => {
                      console.error('Button render error:', error);
                      if (error.type && error.type.includes('invalid_client')) {
                        setError(`Invalid Client ID. Verify "${clientId}" in Google Cloud Console. ` +
                          `Make sure: 1) Client ID is correct, 2) App type is "Web application", 3) OAuth consent screen is configured.`);
                      }
                    }
                  }
                );
              } catch (err) {
                console.error('Error rendering Google button:', err);
                setError('Failed to render Google sign-in button. Please check your Client ID configuration.');
              }
            }
          } catch (err) {
            console.error('Error initializing Google OAuth:', err);
            setError('Failed to initialize Google sign-in. Please check your configuration.');
          }
        }
      } else {
        console.error('Google Identity Services failed to load');
        setError('Google sign-in service failed to load. Please refresh the page.');
      }
    };

    script.onerror = () => {
      console.error('Failed to load Google Identity Services script');
      setError('Failed to load Google sign-in. Please check your internet connection.');
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Initialize Facebook SDK
  useEffect(() => {
    setIsFacebookReady(false);

    if (!facebookAppId) {
      console.warn('⚠️ Facebook App ID not found. Set REACT_APP_FACEBOOK_APP_ID in client env.');
      return;
    }

    // Ensure fb-root exists (recommended by Facebook)
    if (!document.getElementById('fb-root')) {
      const fbRoot = document.createElement('div');
      fbRoot.id = 'fb-root';
      document.body.prepend(fbRoot);
    }

    // If FB is already loaded, init immediately.
    const maybeInit = () => {
      if (!window.FB) return false;
      try {
        // Avoid double-init
        if (!window.__clubfinderFacebookInitialized) {
          window.FB.init({
            appId: facebookAppId,
            cookie: true,
            xfbml: false,
            version: 'v18.0'
          });
          window.__clubfinderFacebookInitialized = true;
          console.log('✅ Facebook SDK initialized');
        }
        setIsFacebookReady(true);
        return true;
      } catch (e) {
        console.error('❌ Failed to initialize Facebook SDK:', e);
        setIsFacebookReady(false);
        return false;
      }
    };

    if (maybeInit()) return;

    // If script tag exists (index.html), wait for it; otherwise inject it.
    const existingScript = document.querySelector('script[src*="connect.facebook.net/en_US/sdk.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      document.body.appendChild(script);
    }

    const interval = setInterval(() => {
      if (maybeInit()) {
        clearInterval(interval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.FB) {
        console.error('❌ Facebook SDK did not load in time');
        setIsFacebookReady(false);
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [facebookAppId]);

  const handleFacebookLogin = async (response) => {
    setError('');
    setIsLoading(true);
    
    console.log('🔐 Starting Facebook OAuth login...');
    console.log('Facebook response:', response);
    
    if (!response || !response.accessToken) {
      console.error('❌ No access token from Facebook');
      setError('Failed to get Facebook access token. Please try again.');
      setIsLoading(false);
      return;
    }
    
    try {
      const response_data = await api.post('/auth/facebook', {
        accessToken: response.accessToken
      });
      
      console.log('📥 Server response received:', response_data.status, response_data.data);
      
      if (response_data.data.success) {
        console.log('✅ Login successful, storing session...');
        const sessionId = response_data.data.sessionId;
        const userData = response_data.data.user;
        setError('');
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('user', JSON.stringify(userData));
        // Store Facebook access token for autofill analysis
        localStorage.setItem('facebook_access_token', response.accessToken);
        if (onLogin) onLogin(userData, sessionId);
        console.log('✅ Redirecting to home...');
        setTimeout(() => navigate('/'), 100);
      } else {
        console.error('❌ Login failed - no success flag');
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ ========== Facebook OAuth Error ==========');
      console.error('Full error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      
      if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        const errorDetails = err.response.data.details;
        setError(sanitizeError(errorMsg + (errorDetails ? ' (' + errorDetails + ')' : '')));
      } else if (err.response?.status === 404) {
        setError('Server not ready. If using Render, wait 60 seconds for cold start, then try again.');
      } else if (err.response?.status === 400) {
        setError('Invalid Facebook token. Please try signing in again.');
      } else if (err.response?.status === 500) {
        setError('Server error during authentication. Please check server logs.');
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError('Unable to connect. Wait 60s for backend to wake up, then try again.');
      } else if (!err.response) {
        setError('No response. Render may be starting—wait 60s and try again.');
      } else {
        setError(sanitizeError('Failed to sign in with Facebook: ' + (err.message || 'Unknown error')));
      }
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    if (provider === 'google') {
      // The button will handle the click via Google's SDK
      const button = document.getElementById('google-signin-button');
      if (button && button.firstChild) {
        button.firstChild.click();
      } else {
        setError('Google Sign-In is loading. Please wait a moment and try again.');
      }
    } else if (provider === 'facebook') {
      // Initialize Facebook SDK and trigger login
      if (!facebookAppId) {
        setError(
          'Facebook login is not configured. Set REACT_APP_FACEBOOK_APP_ID in client env and restart the client.'
        );
        return;
      }

      if (!isFacebookReady) {
        setError('Facebook is still loading. Please wait a moment and try again.');
        return;
      }

      if (window.FB) {
        window.FB.login((response) => {
          if (response.authResponse) {
            handleFacebookLogin(response.authResponse);
          } else {
            console.error('Facebook login failed:', response);
            setError('Facebook login was cancelled or failed. Please try again.');
          }
        }, { scope: 'public_profile' });
      } else {
        setError('Facebook SDK is not loaded. Please refresh the page.');
      }
    } else if (provider === 'apple') {
      handleAppleLogin();
    } else if (provider === 'x' || provider === 'twitter') {
      handleXLogin();
    } else {
      console.log(`Social login with ${provider} - not implemented yet`);
    }
  };

  const handleXLogin = async () => {
    setError('');
    setIsLoading(true);
    
    console.log('🔐 Starting X (Twitter) Sign-In...');
    
    if (!xClientId) {
      setError('X (Twitter) login is not configured. Set REACT_APP_X_CLIENT_ID in client .env file.');
      setIsLoading(false);
      return;
    }

    try {
      // Get authorization URL from server
      // Redirect URI should point to server callback endpoint, not frontend
      const response = await api.get('/auth/x/authorize', {
        params: {
          redirectUri: window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.')
            ? 'http://127.0.0.1:5001/api/auth/x/callback'
            : 'https://clubfinder-api.onrender.com/api/auth/x/callback'
        }
      });

      if (response.data.authorizationUrl) {
        // Redirect to X authorization page
        window.location.href = response.data.authorizationUrl;
      } else {
        setError('Failed to get X authorization URL. Please try again.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('❌ ========== X OAuth Error ==========');
      console.error('Full error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      
      if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        const errorDetails = err.response.data.details;
        setError(sanitizeError(errorMsg + (errorDetails ? ' (' + errorDetails + ')' : '')));
      } else if (err.response?.status === 404) {
        setError('Server not ready. If using Render, wait 60 seconds for cold start, then try again.');
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError('Unable to connect. Wait 60s for backend to wake up, then try again.');
      } else if (!err.response) {
        setError('No response. Render may be starting—wait 60s and try again.');
      } else {
        setError(sanitizeError('Failed to initiate X login: ' + (err.message || 'Unknown error')));
      }
      setIsLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('');
    setIsLoading(true);
    
    console.log('🔐 Starting Apple Sign-In...');
    
    // Check if Apple Sign-In is available
    if (!window.AppleID) {
      setError('Apple Sign-In is not available. Please check your browser and Apple configuration.');
      setIsLoading(false);
      return;
    }

    try {
      // Initialize Apple Sign-In if not already initialized
      const appleRedirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI || window.location.origin + '/login';
      
      if (!appleClientId || appleClientId === 'YOUR_APPLE_SERVICE_ID_HERE') {
        setError('Apple Sign-In is not configured. Set REACT_APP_APPLE_CLIENT_ID in client .env file. See APPLE_SIGNIN_SETUP.md for instructions.');
        setIsLoading(false);
        return;
      }

      // Initialize Apple ID
      if (!window.__appleSignInInitialized) {
        window.AppleID.auth.init({
          clientId: appleClientId,
          scope: 'name email',
          redirectURI: appleRedirectURI,
          usePopup: true
        });
        window.__appleSignInInitialized = true;
      }

      // Sign in with Apple
      const response = await window.AppleID.auth.signIn();
      
      console.log('✅ Apple Sign-In response received:', {
        authorization: response.authorization ? 'Yes' : 'No',
        user: response.user ? 'Yes' : 'No'
      });

      if (response.authorization) {
        const { id_token, code } = response.authorization;

        const response_data = await api.post('/auth/apple', {
          identityToken: id_token,
          authorizationCode: code,
          user: response.user || null
        });

        console.log('📥 Server response received:', response_data.status, response_data.data);

        if (response_data.data.success) {
          const sessionId = response_data.data.sessionId;
          const userData = response_data.data.user;
          setError('');
          localStorage.setItem('sessionId', sessionId);
          localStorage.setItem('user', JSON.stringify(userData));
          if (onLogin) onLogin(userData, sessionId);
          console.log('✅ Redirecting to home...');
          setTimeout(() => navigate('/'), 100);
        } else {
          console.error('❌ Login failed - no success flag');
          setError('Login failed. Please try again.');
        }
      } else {
        console.error('❌ No authorization from Apple');
        setError('Apple Sign-In was cancelled or failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ ========== Apple Sign-In Error ==========');
      console.error('Full error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      
      if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        const errorDetails = err.response.data.details;
        setError(sanitizeError(errorMsg + (errorDetails ? ' (' + errorDetails + ')' : '')));
      } else if (err.response?.status === 404) {
        setError('Server not ready. If using Render, wait 60 seconds for cold start, then try again.');
      } else if (err.response?.status === 400) {
        setError('Invalid Apple token. Please try signing in again.');
      } else if (err.response?.status === 500) {
        setError('Server error during authentication. Please check server logs.');
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setError('Unable to connect. Wait 60s for backend to wake up, then try again.');
      } else if (!err.response) {
        setError('No response. Render may be starting—wait 60s and try again.');
      } else {
        setError(sanitizeError('Failed to sign in with Apple: ' + (err.message || 'Unknown error')));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Google Icon SVG
  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  // Facebook Icon SVG
  const FacebookIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );

  // Apple Icon SVG
  const AppleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );

  // X (Twitter) Icon SVG
  const XIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0f0f0f] py-12 px-6">
      <style>{`
        /* Match Facebook and X buttons to Google button size (Google's large button is 40px) */
        button.social-login-button {
          height: 40px !important;
          min-height: 40px !important;
          max-height: 40px !important;
          font-size: 14px !important;
          padding: 0 24px !important;
          line-height: 1 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        /* Ensure Google button container doesn't affect sizing */
        #google-signin-button {
          height: 40px !important;
        }
        #google-signin-button > div {
          height: 40px !important;
        }
        #google-signin-button iframe {
          height: 40px !important;
        }
      `}</style>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <Link to="/" className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-black">
              <span className="text-white font-bold text-2xl" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', position: 'relative', top: '-2px' }}>C</span>
            </div>
            <span className="text-3xl font-bold text-gray-900 dark:text-[#e5e5e5]">ClubFinder Social</span>
          </Link>
        </div>

        {/* Welcome Heading */}
        <h1 className="text-center text-4xl font-bold text-gray-900 dark:text-[#e5e5e5] mb-12">
          {isLogin ? 'Welcome back' : 'Sign up for ClubFinder Social'}
        </h1>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {!isLogin && (
            <div>
              <input
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-white dark:bg-[#181818] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-[#e5e5e5] rounded-md focus:outline-none focus:border-gray-900 dark:focus:border-gray-400 transition-colors text-base"
                placeholder="Full Name"
              />
            </div>
          )}

          <div>
            <input
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-4 bg-white border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:border-gray-900 transition-colors text-base"
              placeholder="Email or username"
            />
          </div>

          <div>
            <input
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-4 bg-white border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:border-gray-900 transition-colors text-base"
              placeholder="Password"
            />
          </div>

          {!isLogin && (
            <div className="space-y-4 pt-4 border-t border-gray-300 dark:border-[#2d2d2d]">
              <div>
                <input
                  name="school"
                  type="text"
                  value={formData.school}
                  onChange={handleChange}
                  className="w-full px-4 py-4 bg-white dark:bg-[#181818] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-[#e5e5e5] rounded-md focus:outline-none focus:border-gray-900 dark:focus:border-gray-400 transition-colors text-base"
                  placeholder="School (Optional)"
                />
              </div>
              
              <div>
                <input
                  name="hobbies"
                  type="text"
                  value={formData.hobbies}
                  onChange={handleChange}
                  className="w-full px-4 py-4 bg-white dark:bg-[#181818] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-[#e5e5e5] rounded-md focus:outline-none focus:border-gray-900 dark:focus:border-gray-400 transition-colors text-base"
                  placeholder="Hobbies (comma-separated)"
                />
              </div>
              
              <div>
                <input
                  name="majors"
                  type="text"
                  value={formData.majors}
                  onChange={handleChange}
                  className="w-full px-4 py-4 bg-white dark:bg-[#181818] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-[#e5e5e5] rounded-md focus:outline-none focus:border-gray-900 dark:focus:border-gray-400 transition-colors text-base"
                  placeholder="Majors (comma-separated)"
                />
              </div>
              
              <div>
                <input
                  name="street"
                  type="text"
                  value={formData.street}
                  onChange={handleChange}
                  className="w-full px-4 py-4 bg-white dark:bg-[#181818] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-[#e5e5e5] rounded-md focus:outline-none focus:border-gray-900 dark:focus:border-gray-400 transition-colors text-base"
                  placeholder="Street/Area"
                />
              </div>
              
              <div>
                <label className="block text-gray-900 dark:text-[#e5e5e5] text-sm mb-3">Group Preferences</label>
                <div className="space-y-2">
                  {['school', 'public', 'open'].map(pref => (
                    <label key={pref} className="flex items-center text-gray-900 dark:text-[#e5e5e5]">
                      <input
                        type="checkbox"
                        value={pref}
                        checked={formData.preferences.includes(pref)}
                        onChange={handleChange}
                        className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-[#181818] text-green-500 focus:ring-green-500"
                      />
                      <span className="ml-3 text-sm capitalize">{pref} groups</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <textarea
                  name="lookingFor"
                  value={formData.lookingFor}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-4 bg-white border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:border-gray-900 transition-colors resize-none text-base"
                  placeholder="What kind of group are you looking for?"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-[#1DB954] text-white text-base font-bold rounded-full hover:bg-[#1ed760] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : (isLogin ? 'Continue' : 'Sign up')}
          </button>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-[#2d2d2d]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">or</span>
            </div>
          </div>

          <div className="space-y-3">
            <div id="google-signin-button" className="w-full flex justify-center"></div>
            {error && (error.includes('OAuth Configuration') || error.includes('Invalid Client ID') || error.includes('invalid_client')) && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm">
                <p className="font-semibold mb-2">⚠️ Google OAuth Configuration Issue</p>
                {error.includes('Invalid Client ID') || error.includes('invalid_client') ? (
                  <>
                    <p className="mb-2">The Client ID appears to be invalid. Please verify:</p>
                    <ol className="list-decimal list-inside space-y-1 mb-2">
                      <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google Cloud Console</a></li>
                      <li>Find your OAuth 2.0 Client ID</li>
                      <li>Verify the Client ID is: <code className="bg-yellow-100 px-1 rounded">509595359009-v3n6lm7m0k1p8k8a34s2sllvh3jsd4fm.apps.googleusercontent.com</code></li>
                      <li>Make sure the Application type is <strong>"Web application"</strong></li>
                      <li>Check that OAuth consent screen is configured (APIs & Services → OAuth consent screen)</li>
                      <li>Add <code className="bg-yellow-100 px-1 rounded">{window.location.origin}</code> to "Authorized JavaScript origins"</li>
                      <li>Add <code className="bg-yellow-100 px-1 rounded">{window.location.origin}</code> to "Authorized redirect URIs"</li>
                      <li>Click SAVE and wait 2-3 minutes</li>
                    </ol>
                  </>
                ) : (
                  <>
                    <p className="mb-2">To fix this error:</p>
                    <ol className="list-decimal list-inside space-y-1 mb-2">
                      <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google Cloud Console</a></li>
                      <li>Find your OAuth Client ID</li>
                      <li>Add <code className="bg-yellow-100 px-1 rounded">{window.location.origin}</code> to "Authorized JavaScript origins"</li>
                      <li>Add <code className="bg-yellow-100 px-1 rounded">{window.location.origin}</code> to "Authorized redirect URIs"</li>
                      <li>Click SAVE and wait 2-3 minutes</li>
                    </ol>
                  </>
                )}
                <p className="text-xs mt-2">Current origin: <code className="bg-yellow-100 px-1 rounded">{window.location.origin}</code></p>
                <p className="text-xs mt-1">Client ID: <code className="bg-yellow-100 px-1 rounded">509595359009-v3n6lm7m0k1p8k8a34s2sllvh3jsd4fm.apps.googleusercontent.com</code></p>
              </div>
            )}

            <button
              type="button"
              onClick={() => handleSocialLogin('facebook')}
              className="w-full social-login-button bg-white dark:bg-[#181818] text-black dark:text-[#e5e5e5] rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all font-medium flex items-center justify-center space-x-3 border border-gray-300 dark:border-gray-600"
            >
              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <FacebookIcon />
              </div>
              <span>Continue with Facebook</span>
            </button>

            {/* Apple Sign-In - Only show if configured (requires $99/year Apple Developer account) */}
            {(appleClientId && appleClientId !== 'YOUR_APPLE_SERVICE_ID_HERE') && (
              <button
                type="button"
                onClick={() => handleSocialLogin('apple')}
                className="w-full social-login-button bg-white dark:bg-[#181818] text-black dark:text-[#e5e5e5] rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all font-medium flex items-center justify-center space-x-3 border border-gray-300 dark:border-gray-600"
              >
                <AppleIcon />
                <span>Continue with Apple</span>
              </button>
            )}

            {/* X (Twitter) Sign-In */}
            {xClientId && (
              <button
                type="button"
                onClick={() => handleSocialLogin('x')}
                className="w-full social-login-button bg-black text-white rounded-full hover:bg-gray-900 transition-all font-medium flex items-center justify-center space-x-3"
              >
                <XIcon />
                <span>Continue with X</span>
              </button>
            )}
          </div>

          <div className="text-center pt-8">
            <span className="text-gray-600 dark:text-gray-400 text-sm">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
            </span>
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-gray-900 dark:text-[#e5e5e5] hover:underline font-bold text-sm"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
